import { useEffect, useState } from "react";
import { confirmDialog } from "../components/ConfirmDialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { GripVertical, Plus, Pencil, Trash2 } from "lucide-react";

const CATEGORIES = [
  { id: "crypto", label: "Crypto" },
  { id: "stock", label: "Action / Indice" },
  { id: "commodity", label: "Matière" },
  { id: "rate", label: "Taux" },
];
const EMPTY = { key: "", name: "", category: "crypto", cg_id: "", yf_ticker: "", tv_symbol: "", order: 0, enabled: true };

function SortableRow({ card, onEdit, onDelete, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: card.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid={`home-card-row-${card.key}`}>
      <Card className={`p-3 bg-card border-white/5 flex items-center gap-3 ${!card.enabled ? "opacity-50" : ""}`}>
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-zinc-500 hover:text-zinc-300 p-1" aria-label="Réorganiser">
          <GripVertical className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono-num text-emerald-300 font-medium">{card.key}</span>
            <span className="text-zinc-400 truncate">{card.name}</span>
            <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 px-2 py-0.5 bg-white/5 rounded">
              {card.category}
            </span>
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5 font-mono">
            {card.cg_id && <>cg={card.cg_id} · </>}
            {card.yf_ticker && <>yf={card.yf_ticker} · </>}
            tv={card.tv_symbol}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Switch
            checked={card.enabled}
            onCheckedChange={(v) => onToggle(card, v)}
            data-testid={`home-card-toggle-${card.key}`}
          />
          <Button variant="ghost" size="sm" onClick={() => onEdit(card)} data-testid={`home-card-edit-${card.key}`}>
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-rose-400 hover:bg-rose-500/10"
            onClick={async () => (await confirmDialog({ title: `Retirer ${card.key} de l'accueil ?`, confirmLabel: "Retirer" })) && onDelete(card.id)}
            data-testid={`home-card-delete-${card.key}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </Card>
    </div>
  );
}

function CardForm({ open, onOpenChange, initial, onSubmit, submitting }) {
  const [form, setForm] = useState(initial || EMPTY);
  useEffect(() => setForm(initial || EMPTY), [initial]);
  const bind = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const isCrypto = form.category === "crypto";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-card border-white/10">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Modifier la carte" : "Nouvelle carte"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Ticker affiché</Label>
              <Input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value.toUpperCase() })} placeholder="BTC" data-testid="home-card-form-key" />
            </div>
            <div>
              <Label>Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="home-card-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Nom complet</Label>
            <Input value={form.name} onChange={bind("name")} placeholder="Bitcoin" data-testid="home-card-form-name" />
          </div>
          {isCrypto ? (
            <div>
              <Label>ID CoinGecko</Label>
              <Input value={form.cg_id || ""} onChange={bind("cg_id")} placeholder="bitcoin, ethereum, solana…" data-testid="home-card-form-cgid" />
              <p className="text-[11px] text-zinc-500 mt-1">Fallback yfinance intégré (BTC-USD, ETH-USD…).</p>
            </div>
          ) : (
            <div>
              <Label>Ticker yfinance</Label>
              <Input value={form.yf_ticker || ""} onChange={bind("yf_ticker")} placeholder="SPY, ^TNX, GC=F, CL=F…" data-testid="home-card-form-yf" />
            </div>
          )}
          <div>
            <Label>TradingView symbol (popup)</Label>
            <Input value={form.tv_symbol} onChange={bind("tv_symbol")} placeholder="BINANCE:BTCUSDT, AMEX:SPY…" data-testid="home-card-form-tv" />
          </div>
          <div className="flex items-center gap-3 pt-2">
            <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
            <Label className="cursor-pointer">Actif (visible sur l'accueil)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/10">Annuler</Button>
          <Button
            onClick={() => {
              if (!form.key.trim() || !form.name.trim() || !form.tv_symbol.trim()) return toast.error("Key, nom et TradingView symbol requis");
              if (isCrypto && !form.cg_id) return toast.error("cg_id requis pour crypto");
              if (!isCrypto && !form.yf_ticker) return toast.error("yf_ticker requis pour non-crypto");
              onSubmit(form);
            }}
            disabled={submitting}
            className="bg-emerald-500 text-black hover:bg-emerald-300"
            data-testid="home-card-form-submit"
          >
            {submitting ? "…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminHomeCardsPage() {
  const qc = useQueryClient();
  const [items, setItems] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data: cards = [], isLoading } = useQuery({
    queryKey: ["admin-home-cards"],
    queryFn: async () => (await api.get("/admin/home-cards")).data,
  });

  useEffect(() => { setItems(cards); }, [cards]);

  const reorderMut = useMutation({
    mutationFn: async (ordered_ids) => (await api.post("/admin/home-cards/reorder", { ordered_ids })).data,
    onSuccess: () => toast.success("Ordre enregistré"),
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
  const upsertMut = useMutation({
    mutationFn: async (form) => {
      if (form.id) return (await api.patch(`/admin/home-cards/${form.id}`, form)).data;
      return (await api.post("/admin/home-cards", form)).data;
    },
    onSuccess: () => {
      toast.success("Carte enregistrée");
      qc.invalidateQueries({ queryKey: ["admin-home-cards"] });
      setDlgOpen(false);
      setEditing(null);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/home-cards/${id}`)).data,
    onSuccess: () => {
      toast.success("Retirée");
      qc.invalidateQueries({ queryKey: ["admin-home-cards"] });
    },
  });
  const toggleMut = useMutation({
    mutationFn: async ({ card, enabled }) => (await api.patch(`/admin/home-cards/${card.id}`, { ...card, enabled })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-home-cards"] }),
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((i) => i.id === active.id);
    const newIdx = items.findIndex((i) => i.id === over.id);
    const next = arrayMove(items, oldIdx, newIdx);
    setItems(next);
    reorderMut.mutate(next.map((i) => i.id));
  };

  return (
    <div className="mx-auto max-w-4xl px-6 py-10">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-2">Admin</div>
          <h1 className="font-display font-black text-3xl">Cartes d'actifs — Accueil</h1>
          <p className="mt-2 text-sm text-zinc-400 max-w-lg">
            Glissez pour réordonner. Basculez l'interrupteur pour masquer/afficher une carte sans la supprimer.
          </p>
        </div>
        <Button
          onClick={() => { setEditing(null); setDlgOpen(true); }}
          className="bg-emerald-500 text-black hover:bg-emerald-300"
          data-testid="home-cards-new"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Ajouter
        </Button>
      </div>

      {isLoading ? (
        <div className="text-zinc-500">Chargement…</div>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center bg-card border-white/5 text-zinc-500">Aucune carte configurée.</Card>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <div className="grid gap-2" data-testid="home-cards-sortable">
              {items.map((c) => (
                <SortableRow
                  key={c.id}
                  card={c}
                  onEdit={(card) => { setEditing(card); setDlgOpen(true); }}
                  onDelete={(id) => deleteMut.mutate(id)}
                  onToggle={(card, enabled) => toggleMut.mutate({ card, enabled })}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {dlgOpen && (
        <CardForm
          open={dlgOpen}
          onOpenChange={(v) => { setDlgOpen(v); if (!v) setEditing(null); }}
          initial={editing}
          onSubmit={upsertMut.mutate}
          submitting={upsertMut.isPending}
        />
      )}
    </div>
  );
}
